import React from "react";
import styles from "./TermService.module.css";
import { TUITION_PAYMENT_CONFIG } from "../../../utils/tuitionPaymentConfig";

const SECTION_LINKS = [
  { id: "terms-section-1", label: "General" },
  { id: "terms-section-2", label: "Students" },
  { id: "terms-section-3", label: "Tutors" },
  { id: "terms-section-4", label: "Institutes" },
  { id: "terms-section-5", label: "Payments" },
  { id: "terms-section-6", label: "Subscription" },
  { id: "terms-section-7", label: "Privacy" },
  { id: "terms-section-8", label: "Liability" },
  { id: "terms-section-9", label: "Acceptance" },
];

const scrollToTermsSection = (sectionId) => {
  document.getElementById(sectionId)?.scrollIntoView({
    behavior: "smooth",
    block: "start",
  });
};

const TermService = ({ inModal = false }) => {
  const { minimumMonthsCharge, platformFeePercent } = TUITION_PAYMENT_CONFIG;

  return (
    <div
      className={`${styles.termsContainer} ${inModal ? styles.termsContainerModal : ""}`}
    >
      {!inModal && (
        <div className={styles.termsHeader}>
          <h1 className={styles.termsTitle}>Terms & Conditions</h1>
          <p className={styles.termsSubtitle}>
            Please read these terms carefully before registering or using Book My
            Tuitions.
          </p>
        </div>
      )}

      <div className={`${styles.termsContent} ${inModal ? styles.termsContentModal : ""}`}>
        {inModal && (
          <nav className={styles.termsToc} aria-label="Terms sections">
            <p className={styles.termsTocTitle}>Jump to a section</p>
            <div className={styles.termsTocLinks}>
              {SECTION_LINKS.map((section) => (
                <button
                  key={section.id}
                  type="button"
                  className={styles.termsTocButton}
                  onClick={() => scrollToTermsSection(section.id)}
                >
                  {section.label}
                </button>
              ))}
            </div>
          </nav>
        )}

        <section id="terms-section-1" className={styles.termsSection}>
          <h2>1. General Platform Terms</h2>
          <ul>
            <li>
              Book My Tuitions is an online technology platform that connects
              students, tutors, and institutes for live tuition.
            </li>
            <li>
              All classes booked through the platform are conducted online. Users
              join live sessions through platform-assigned class links after
              required approvals and, where applicable, verified payment.
            </li>
            <li>
              Book My Tuitions facilitates discovery, booking, communication,
              session coordination, and tuition fee collection for regular
              tuition enrolments.
            </li>
            <li>
              The platform is not an employer, agent, or guarantor of tutors,
              institutes, teaching quality, academic results, or session
              outcomes.
            </li>
            <li>
              Users must provide accurate registration, profile, and payment
              information. Misuse, fraud, or repeated violations may lead to
              suspension or termination.
            </li>
            <li>
              Tutor and institute subscription fees are charged for platform
              access and related services. Subscription fees are generally
              non-refundable unless required by applicable law.
            </li>
            <li>
              Book My Tuitions may update these terms, fee policies, and
              platform features from time to time. Continued use after updates
              constitutes acceptance of the revised terms.
            </li>
          </ul>
        </section>

        <section id="terms-section-2" className={styles.termsSection}>
          <h2>2. Student Terms & Conditions</h2>
          <ul>
            <li>
              Students must provide valid personal and contact details and keep
              their learning preferences up to date.
            </li>
            <li>
              Students may book free online demo sessions before enrolling in
              regular tuition, subject to tutor availability and platform
              rules.
            </li>
            <li>
              Regular tuition begins only after tutor approval and completion
              of the platform payment process described in these terms.
            </li>
            <li>
              Students are responsible for reviewing tutor profiles,
              qualifications, fees, and availability before booking.
            </li>
            <li>
              For regular tuition, students must pay the applicable tuition fee
              through the platform UPI payment flow and submit the transaction
              reference for admin verification.
            </li>
            <li>
              Class access for regular tuition is unlocked only after payment is
              verified by the platform admin and the tuition is planned with an
              assigned online class link.
            </li>
            <li>
              Students must attend online sessions on time, use assigned class
              links only for authorised sessions, and maintain respectful
              conduct with tutors and platform staff.
            </li>
            <li>
              Fee disputes, teaching quality concerns, and session-related issues
              should first be raised through platform support. Book My Tuitions
              does not guarantee academic outcomes.
            </li>
            <li>
              Fake bookings, misuse of tutor contact details, or payment fraud
              may result in account suspension.
            </li>
          </ul>
        </section>

        <section id="terms-section-3" className={styles.termsSection}>
          <h2>3. Tutor Terms & Conditions</h2>
          <ul>
            <li>
              Tutors must provide accurate qualification, experience, subject,
              availability, and fee information in their profile.
            </li>
            <li>
              Tutors may register independently or through an institute, where
              applicable.
            </li>
            <li>
              All tuition delivered through Book My Tuitions must be conducted
              online using platform-assigned session links unless explicitly
              approved otherwise by the platform.
            </li>
            <li>
              Tutors are responsible for session delivery, punctuality,
              professional conduct, and the teaching quality offered to
              students.
            </li>
            <li>
              Tutors must approve or reject regular tuition requests in a
              timely manner and honour agreed schedules after approval.
            </li>
            <li>
              Tutor fees displayed on the platform are used to calculate the
              student payment amount for regular tuition bookings.
            </li>
            <li>
              Book My Tuitions is not an employer of tutors. Tutors remain
              independent service providers or institute-associated educators, as
              applicable.
            </li>
            <li>
              Tutors must maintain valid KYC and educational documents where
              requested and comply with applicable laws.
            </li>
            <li>
              Misrepresentation, repeated no-shows, or policy violations may lead
              to profile suspension or removal.
            </li>
          </ul>
        </section>

        <section id="terms-section-4" className={styles.termsSection}>
          <h2>4. Institute Terms & Conditions</h2>
          <ul>
            <li>
              Institutes must provide valid registration, ownership, and contact
              details.
            </li>
            <li>
              Institutes are responsible for tutors added under their account,
              including verification, conduct, and session quality.
            </li>
            <li>
              All tuition coordinated through the platform must follow the
              online delivery model and platform booking rules.
            </li>
            <li>
              Institutes must maintain accurate subject, timing, and tutor
              capacity information.
            </li>
            <li>
              Institute subscription and tutor capacity limits apply as per the
              selected subscription plan.
            </li>
            <li>
              Book My Tuitions does not guarantee student inflow, bookings, or
              revenue for institutes.
            </li>
            <li>
              Repeated violations, false listings, or misuse of student data may
              result in account deactivation.
            </li>
          </ul>
        </section>

        <section id="terms-section-5" className={styles.termsSection}>
          <h2>5. Tuition Payments & Fee Policy</h2>
          <ul>
            <li>
              Regular tuition payments are collected through the platform UPI
              payment process after tutor approval of the enrolment.
            </li>
            <li>
              The payable amount is calculated from the tutor&apos;s listed fee
              and is charged for a minimum of {minimumMonthsCharge} months per
              regular tuition enrolment, unless otherwise shown at checkout.
            </li>
            <li>
              Students must pay using the UPI details or QR code shown on the
              platform and submit the transaction reference for verification.
            </li>
            <li>
              Payments remain pending until verified by Book My Tuitions admin.
              Rejected payments must be corrected and resubmitted.
            </li>
            <li>
              A platform service fee of {platformFeePercent}% applies to verified
              tuition payments. The remaining balance is allocated to the tutor
              side of the transaction.
            </li>
            <li>
              Tutor payouts are processed by the platform after payment
              verification, subject to internal review and applicable payout
              rules.
            </li>
            <li>
              Demo sessions are free unless explicitly stated otherwise on the
              platform.
            </li>
            <li>
              Book My Tuitions may update payment methods, fee calculation
              rules, or verification timelines with reasonable notice where
              required.
            </li>
          </ul>
        </section>

        <section id="terms-section-6" className={styles.termsSection}>
          <h2>6. Subscription & Platform Usage Policy</h2>
          <ul>
            <li>
              Tutors and institutes must purchase an active subscription to
              access full platform features, including visibility, booking
              tools, and session management.
            </li>
            <li>
              Subscriptions provide platform access only and do not guarantee
              students, bookings, earnings, or session volume.
            </li>
            <li>
              Subscriptions are time-based. Expired subscriptions may restrict
              access until renewal.
            </li>
            <li>
              Students may require an active account and completed profile to
              search tutors, book demos, and enrol in regular tuition.
            </li>
          </ul>
        </section>

        <section id="terms-section-7" className={styles.termsSection}>
          <h2>7. Privacy Policy</h2>
          <ul>
            <li>
              Book My Tuitions collects information you provide during
              registration, profile creation, booking, payment, and support
              interactions.
            </li>
            <li>
              Personal data is used to operate the platform, verify users,
              process bookings and payments, coordinate sessions, and provide
              customer support.
            </li>
            <li>
              Contact details may be shared between matched students and tutors
              only as needed for platform bookings and session coordination.
            </li>
            <li>
              Payment references and verification records are stored for
              accounting, fraud prevention, and dispute handling.
            </li>
            <li>
              Users must not copy, scrape, resell, or misuse contact information
              obtained through the platform.
            </li>
            <li>
              We use reasonable technical and organisational measures to protect
              user data, but no online system can be guaranteed completely
              secure.
            </li>
          </ul>
        </section>

        <section id="terms-section-8" className={styles.termsSection}>
          <h2>8. Limitation of Liability</h2>
          <ul>
            <li>
              Book My Tuitions is not liable for academic performance, teaching
              quality disputes, internet connectivity issues, third-party video
              service interruptions, or direct financial arrangements made
              outside the platform.
            </li>
            <li>
              Users interact and attend online sessions at their own risk.
            </li>
            <li>
              Platform liability is limited to the extent permitted by law and,
              where applicable, to the services directly provided through the
              platform.
            </li>
          </ul>
        </section>

        <section
          id="terms-section-9"
          className={`${styles.termsSection} ${styles.highlight}`}
        >
          <h2>9. Acceptance of Terms</h2>
          <p>
            By registering, creating a profile, checking the acceptance box, or
            clicking <strong>&quot;I Agree&quot;</strong>, you confirm that you
            have read, understood, and agreed to these Terms & Conditions and
            Privacy Policy.
          </p>
        </section>
      </div>
    </div>
  );
};

export default TermService;
